import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import {
  ResponsiveAppShell,
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyCard,
  TaskyCardHeader,
  TaskyErrorState,
  TaskyProgress,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTopBar,
  TaskyPageContainer,
} from '../../components/tasky';
import ComparisonCard from '../../components/banner-ab-test/ComparisonCard';
import FinalRecommendationCard from '../../components/banner-ab-test/FinalRecommendationCard';
import RoundTimeline from '../../components/banner-ab-test/RoundTimeline';
import { useGenerationPolling } from '../../hooks/useGenerationPolling';
import { useToast } from '../../contexts/ToastContext';
import { cancelBannerAbTest, getBannerAbTest } from '../../services/api/bannerAbTestService';
import {
  BANNER_AB_ACTIVE_STATUSES,
  BANNER_AB_DISCLAIMER,
  BANNER_AB_PREFERENCE_NOTE,
  BANNER_AB_STATUS_LABELS,
  BANNER_AB_STATUS_VARIANTS,
  BANNER_AB_STOPPED_REASON_LABELS,
  type BannerAbImageSummary,
  type BannerAbTest,
  type BannerAbTestStatus,
} from '../../types/bannerAbTest';

const ACTIVE = new Set<BannerAbTestStatus>(BANNER_AB_ACTIVE_STATUSES);

const STEP_LABELS: Record<string, string> = {
  persona_pool: '仮想顧客を生成しています',
  voting: '採点しています',
  hypothesize: '改善仮説を立てています',
  generating: '派生案を生成しています',
};

/** オートバナーABテストの結果ページ。進行中は統合ポーリングで進捗を追い、終端で詳細を取り直す */
const BannerAbTestResultPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const testId = Number(id);
  const [test, setTest] = useState<BannerAbTest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { showToast } = useToast();

  const load = useCallback(async () => {
    try {
      setTest(await getBannerAbTest(testId));
      setError(null);
    } catch {
      setError('ABテストの読み込みに失敗しました');
    }
  }, [testId]);

  const isActive = !!test && ACTIVE.has(test.status);
  const isLoop = test?.mode === 'auto_loop';
  // 比較に載っているサムネを1回だけ索引化する（PR1 の線形探索 imageById() を置き換え）。
  // 画像の出どころは詳細 API の comparisons だけなので、比較に出ていない画像は引けない
  const imagesById = useMemo(() => {
    const map: Record<number, BannerAbImageSummary> = {};
    for (const c of test?.comparisons ?? []) {
      if (c.image_a) map[c.image_a.id] = c.image_a;
      if (c.image_b) map[c.image_b.id] = c.image_b;
    }
    return map;
  }, [test?.comparisons]);
  const polling = useGenerationPolling('banner_ab_test', isActive ? String(testId) : null, {
    interval: 3000,
    onComplete: () => { load(); },
    onFailed: () => { load(); },
  });

  useEffect(() => { load(); }, [load]);
  // 採点の途中経過（票数）は詳細 API にしか無いので、ポーリングの進捗が動くたびに取り直す
  useEffect(() => { if (isActive) load(); }, [polling.progress, polling.step, isActive, load]);

  // ポーリングが「もう動いていない」と言い出したのに、ページ側はまだ進行中だと思っている＝
  // 別経路で状態が変わっている（他のクライアントからのキャンセル等）。onComplete / onFailed は
  // completed / failed にしか反応しないので、立ち下がりで一度だけ取り直す
  const reloadedOnStopRef = useRef(false);
  useEffect(() => {
    if (polling.isGenerating) {
      reloadedOnStopRef.current = false;
      return;
    }
    if (!isActive || reloadedOnStopRef.current) return;
    reloadedOnStopRef.current = true;
    load();
  }, [polling.isGenerating, isActive, load]);

  const cancel = async () => {
    if (!test) return;
    try {
      await cancelBannerAbTest(test.id);
      showToast('ABテストをキャンセルしました');
      load();
    } catch {
      showToast('キャンセルに失敗しました', 'error');
    }
  };

  // 他ページ（バナー広告 / ムービー制作）と同じ共通シェル。読み込み中・失敗時も同じ枠に収める。
  // デスクトップヘッダーは lg 以上だけに出し、モバイルは ResponsiveAppShell の
  // トップバー（topbarTitle / topbarActions）側に同じ内容を渡す。
  const shell = (title: React.ReactNode, actions: React.ReactNode, body: React.ReactNode) => (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle={title}
      topbarActions={(
        <>
          {/* モバイルはデスクトップヘッダーが出ないので、戻る導線をここに置く */}
          <Link
            to="/ads/banner-ab-tests"
            aria-label="ABテスト一覧へ"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-primary hover:bg-primary/10"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          {actions}
        </>
      )}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-white lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title={title}
          leading={(
            <Link
              to="/ads/banner-ab-tests"
              className="inline-flex shrink-0 items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" /> ABテスト一覧
            </Link>
          )}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        >
          {actions}
        </TaskyTopBar>

        {/* Main Scrollable Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer className="space-y-6">
            {body}
          </TaskyPageContainer>
        </div>
      </div>
    </ResponsiveAppShell>
  );

  // 一度読めていれば、以降の一過性の失敗でページごと消さずに小さく知らせるだけにする
  if (error && !test) {
    return shell('オートバナーABテスト', null, <TaskyErrorState title={error} />);
  }
  if (!test) {
    return shell('オートバナーABテスト', null, (
      <div className="flex justify-center py-12">
        <TaskySpinner size="lg" />
      </div>
    ));
  }

  const winner = test.result?.winner_image_id ?? test.winner_image_id;
  const winnerImage = winner == null ? undefined : imagesById[winner];

  // 最終推奨カードが停止理由を持つのは completed + result のときだけ。cancelled / failed の
  // ループは result を持たないのでカードごと出ず、バッジだけでは deadline / error と区別が付かない
  const showsFinalCard = isLoop && test.status === 'completed' && !!test.result;
  const summary: Array<{ label: string; value: React.ReactNode }> = [
    { label: '仮想顧客', value: `${test.persona_count} 体` },
    { label: '候補', value: `${test.candidate_image_ids.length} 枚` },
    { label: '消費クレジット', value: test.credits_consumed },
    {
      // 予算を指定したループの credits_estimated は「予算まで」の額。最大ラウンドまで回した額とは限らない
      label: isLoop ? '見積り（予算上限まで）' : '見積り',
      value: test.credits_estimated,
    },
  ];
  if (isLoop && test.stopped_reason && !showsFinalCard) {
    summary.push({
      label: '停止理由',
      value: BANNER_AB_STOPPED_REASON_LABELS[test.stopped_reason] ?? test.stopped_reason,
    });
  }

  return shell(
    <>オートバナーABテスト #{test.id}</>,
    <>
      <TaskyBadge variant={BANNER_AB_STATUS_VARIANTS[test.status]}>{BANNER_AB_STATUS_LABELS[test.status]}</TaskyBadge>
      {isActive && <TaskyButton size="sm" variant="outline" onClick={cancel}>キャンセル</TaskyButton>}
    </>,
    <>
      {error && (
        <TaskyAlert tone="error">
          {isActive ? '最新の状態を取得できませんでした。再試行しています…' : error}
        </TaskyAlert>
      )}

      <TaskyCard data-tutorial="ads-banner-ab-tests-summary" variant="warm" padding="sm">
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {summary.map((item) => (
            <div key={item.label}>
              <dt className="text-xs text-gray-500">{item.label}</dt>
              <dd className="text-sm font-medium text-dark">{item.value}</dd>
            </div>
          ))}
        </dl>
      </TaskyCard>

      {isActive && (
        <div>
          <TaskyProgress value={polling.progress || test.progress} />
          <p className="text-xs text-gray-500 mt-1">
            {(polling.step && STEP_LABELS[polling.step]) || ''}
            {isLoop && polling.data?.round ? `　ラウンド ${polling.data.round} / ${polling.data.max_rounds}` : ''}
          </p>
        </div>
      )}
      {test.status === 'failed' && (
        <TaskyErrorState title={test.error_message || 'ABテストに失敗しました'} />
      )}

      {/* ループは最終推奨（＋停止理由）とラウンドの経過が主役。順位表は pairwise のときだけ出す */}
      {showsFinalCard && test.result && (
        <FinalRecommendationCard result={test.result} imagesById={imagesById} />
      )}
      {isLoop && (test.rounds ?? []).length > 0 && (
        <RoundTimeline rounds={test.rounds ?? []} comparisons={test.comparisons ?? []} imagesById={imagesById} />
      )}

      {/* テーブル自身が枠を持つので、カード側は枠なし（枠の二重描画を避ける） */}
      {test.status === 'completed' && test.result && !isLoop && (
        <TaskyCard data-tutorial="ads-banner-ab-tests-ranking" variant="plain" padding="none">
          <TaskyCardHeader>
            <h2 className="text-lg font-semibold text-dark">
              {winner ? '推奨バナー' : '優劣を判定できませんでした'}
            </h2>
          </TaskyCardHeader>
          {winnerImage && (
            <img
              src={winnerImage.thumbnail_url || winnerImage.url}
              alt="推奨バナー"
              className="mb-4 h-48 rounded-tasky-sm bg-warm-surface object-contain"
            />
          )}
          <TaskyTable>
            <TaskyTableHead>
              <tr>
                <TaskyTableHeaderCell>画像</TaskyTableHeaderCell>
                <TaskyTableHeaderCell align="right">勝ち</TaskyTableHeaderCell>
                <TaskyTableHeaderCell align="right">負け</TaskyTableHeaderCell>
                <TaskyTableHeaderCell align="right">平均差</TaskyTableHeaderCell>
                <TaskyTableHeaderCell align="right">選ばれた割合</TaskyTableHeaderCell>
              </tr>
            </TaskyTableHead>
            <TaskyTableBody>
              {test.result.ranking.map((r) => (
                <TaskyTableRow key={r.image_id}>
                  <TaskyTableCell className="font-medium">
                    #{r.image_id}{r.image_id === winner ? '（推奨）' : ''}
                  </TaskyTableCell>
                  <TaskyTableCell align="right">{r.wins}</TaskyTableCell>
                  <TaskyTableCell align="right">{r.losses}</TaskyTableCell>
                  <TaskyTableCell align="right">{r.avg_delta == null ? '-' : r.avg_delta.toFixed(2)}</TaskyTableCell>
                  <TaskyTableCell align="right">{r.avg_preference == null ? '-' : `${Math.round(r.avg_preference * 100)}%`}</TaskyTableCell>
                </TaskyTableRow>
              ))}
            </TaskyTableBody>
          </TaskyTable>
        </TaskyCard>
      )}

      <div className="space-y-4">
        {(test.comparisons ?? []).map((c) => <ComparisonCard key={c.id} comparison={c} />)}
      </div>

      <p className="text-xs text-gray-500">{BANNER_AB_PREFERENCE_NOTE}</p>
      <p className="text-xs text-gray-500">{BANNER_AB_DISCLAIMER}</p>
    </>,
  );
};

export default BannerAbTestResultPage;
